import React, { useEffect, useState } from 'react'

/**
 * SofVerifyModal — SVCP §5.2 structured reason gate when a CO transitions
 * a case or patron's source-of-funds status to `verified`.
 *
 * Used by any surface where a CO can move SoF to verified:
 *   - InvestigationCanvasStrategy SoF select (via InvestigationCase)
 *   - Watchlist SoF select (future; backend already gated)
 *
 * Pass subjectLabel for context (e.g. "Case C-2026-0042" or
 * "Patron Jane Doe (M123)"). Calling onConfirm resolves with a
 * `{ change_reason_code, change_reason_notes }` payload; the caller
 * is responsible for sending the PATCH and closing the modal.
 *
 * Modal is a no-op when `open` is false.
 */

// Must stay in sync with the enum in migration 141 and the ALLOWED_REASON_CODES
// Set in backend/src/routes/cases.js + watchlist.js.
export const SOF_CHANGE_REASON_OPTIONS = [
  {
    value: 'documentation_verified',
    label: 'Documentation verified',
    blurb: 'Payslips / bank statements / tax records reviewed and consistent with declared source.',
  },
  {
    value: 'third_party_confirmed',
    label: 'Third party confirmed',
    blurb: 'Employer / bank / financial adviser independently confirmed the declared source.',
  },
  {
    value: 'transaction_pattern_consistent',
    label: 'Transaction pattern consistent',
    blurb: 'Gaming / deposit / payout history matches the declared source over time.',
  },
  {
    value: 'wealth_assessment_satisfactory',
    label: 'Wealth assessment satisfactory',
    blurb: 'For PEPs / high-wealth patrons: source-of-wealth narrative corroborated with independent evidence.',
  },
  {
    value: 'risk_accepted_monitoring',
    label: 'Residual risk accepted — monitoring',
    blurb: 'Minor gaps remain but residual risk is accepted; enhanced monitoring plan in place.',
  },
  {
    value: 'other',
    label: 'Other (notes required)',
    blurb: 'Use sparingly — prefer one of the structured categories if it fits.',
  },
]

const MIN_NOTES_CHARS = 10

export default function SofVerifyModal({
  open,
  subjectLabel,
  onCancel,
  onConfirm,
  submitting = false,
}) {
  const [reasonCode, setReasonCode] = useState('')
  const [notes, setNotes] = useState('')

  useEffect(() => {
    if (!open) {
      setReasonCode('')
      setNotes('')
    }
  }, [open])

  if (!open) return null

  const trimmedLen = notes.trim().length
  const notesRequiredAndMissing = reasonCode === 'other' && trimmedLen < MIN_NOTES_CHARS
  const canSubmit = !submitting && !!reasonCode && !notesRequiredAndMissing
  const activeOption = SOF_CHANGE_REASON_OPTIONS.find((o) => o.value === reasonCode)

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 540,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
            Mark source of funds as verified
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            SVCP §5.2 — moving {subjectLabel || 'this record'} to verified is a trust assertion.
            Pick the category that best reflects why the declared source holds.
          </div>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
              Verification basis <span style={{ color: '#b91c1c' }}>*</span>
            </label>
            <select
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              disabled={submitting}
              style={{ width: '100%', padding: 8, fontSize: 13, borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', fontFamily: 'inherit' }}
            >
              <option value="">Select a basis…</option>
              {SOF_CHANGE_REASON_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {activeOption && (
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>{activeOption.blurb}</div>
            )}
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
                Supporting notes {reasonCode === 'other' && <span style={{ color: '#b91c1c' }}>*</span>}
              </label>
              <span style={{ fontSize: 11, fontFamily: 'monospace', color: reasonCode === 'other' ? (trimmedLen >= MIN_NOTES_CHARS ? '#16a34a' : '#b91c1c') : '#94a3b8' }}>
                {reasonCode === 'other' ? `${trimmedLen} / ${MIN_NOTES_CHARS} min` : 'optional'}
              </span>
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
              rows={3}
              placeholder={reasonCode === 'other' ? 'Describe why none of the structured categories apply.' : 'Optional — evidence reference, reviewer notes, etc.'}
              style={{ width: '100%', padding: 10, fontSize: 13, borderRadius: 6, border: '1px solid #cbd5e1', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' }}
            />
            {notesRequiredAndMissing && trimmedLen > 0 && (
              <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}>Minimum {MIN_NOTES_CHARS} characters required when the basis is "Other".</div>
            )}
          </div>
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#f8fafc' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, color: '#334155', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, cursor: submitting ? 'not-allowed' : 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              if (!canSubmit) return
              onConfirm({ change_reason_code: reasonCode, change_reason_notes: notes.trim() || undefined })
            }}
            disabled={!canSubmit}
            style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, color: '#fff', background: canSubmit ? '#16a34a' : '#cbd5e1', border: 'none', borderRadius: 6, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
          >
            {submitting ? 'Verifying…' : 'Confirm verified'}
          </button>
        </div>
      </div>
    </div>
  )
}
